import { XMarkIcon } from '@heroicons/react/24/outline';
import Lottie from 'lottie-react';
import { useEffect, useRef } from 'react';

import catAnimation from '@/common/assets/cat-animation.json';
import { useAlertStore, Alert as AlertType } from '@/common/stores/useAlertStore';

const alertStyles: Record<AlertType['type'], string> = {
  success: 'bg-green-50/95 text-green-800',
  danger: 'bg-red-50/95 text-red-800',
  warning: 'bg-yellow-50/95 text-yellow-800',
  info: 'bg-blue-50/95 text-blue-800',
};

const Alert = ({ alert }: { alert: AlertType }) => {
  const removeAlert = useAlertStore((state) => state.removeAlert);
  const alertRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    // Start with opacity 0 and scale down
    if (alertRef.current && contentRef.current) {
      alertRef.current.style.opacity = '0';
      contentRef.current.style.transform = 'scale(0.95)';

      // Trigger animation after a small delay
      requestAnimationFrame(() => {
        alertRef.current!.style.opacity = '1';
        contentRef.current!.style.transform = 'scale(1)';
      });
    }

    // Set up the timeout for auto-close
    const duration = alert.duration * 1000;
    const fadeOutDuration = 500; // match the CSS transition duration

    timeoutRef.current = setTimeout(() => {
      // Start fade out
      if (alertRef.current && contentRef.current) {
        alertRef.current.style.opacity = '0';
        contentRef.current.style.transform = 'scale(0.95)';
      }

      // Remove after fade out completes
      setTimeout(() => {
        removeAlert(alert.id);
      }, fadeOutDuration);
    }, duration - fadeOutDuration); // Start fade out before duration ends

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [alert.duration, alert.id, removeAlert]);

  const handleClose = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (alertRef.current && contentRef.current) {
      alertRef.current.style.opacity = '0';
      contentRef.current.style.transform = 'scale(0.95)';

      setTimeout(() => {
        removeAlert(alert.id);
      }, 500); // match the CSS transition duration
    }
  };

  return (
    <div
      ref={alertRef}
      className={`
        fixed inset-0 flex flex-col items-center justify-center p-4
        transition-all duration-500 ease-in-out
        backdrop-blur-[2px]
        ${alertStyles[alert.type]}
      `}
      role="alert"
    >
      <div
        ref={contentRef}
        className="w-full max-w-2xl transform transition-all duration-500 ease-in-out"
      >
        <div className="relative flex flex-col items-center">
          <button
            className="absolute top-0 right-0 p-2 rounded-full hover:bg-black/10 transition-colors"
            onClick={handleClose}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>

          <div className="w-48 h-48 mb-2 opacity-95">
            <Lottie animationData={catAnimation} loop={true} />
          </div>

          <div className="text-center">
            <div className="text-xl font-medium mb-2">{alert.message}</div>
            {alert.component && <div className="mt-4">{alert.component}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export const AlertContainer = () => {
  const alerts = useAlertStore((state) => state.alerts);

  if (alerts.length === 0) return null;

  return (
    <div className="fixed inset-0 z-50">
      {alerts.map((alert) => (
        <Alert key={alert.id} alert={alert} />
      ))}
    </div>
  );
};
